import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import router from '@/router';
import { getCurrentLocale } from '@/i18n';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v3';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true, // Send cookies with requests
});

// Token refresh state
let isRefreshing = false;
let refreshSubscribers: (() => void)[] = [];

const subscribeTokenRefresh = (callback: () => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = () => {
  refreshSubscribers.forEach((callback) => callback());
  refreshSubscribers = [];
};

const onRefreshFailed = () => {
  refreshSubscribers = [];
};

// Request interceptor to add company ID header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore();

    // Silently drop non-auth requests after logout (never resolve, never reject)
    if (!authStore.isAuthenticated && !config.url?.includes('/auth/')) {
      return new Promise(() => {});
    }

    // Add company ID header (tokens are sent via httpOnly cookies)
    if (authStore.currentCompanyId) {
      config.headers['X-Company-Id'] = authStore.currentCompanyId;
    }

    // Send language preference for backend error translation
    config.headers['Accept-Language'] = getCurrentLocale();

    // Disable caching for GET requests
    if (config.method === 'get') {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Silently swallow cancelled requests (e.g. from logout)
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Don't try to refresh tokens for auth endpoints
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                          originalRequest.url?.includes('/auth/register') ||
                          originalRequest.url?.includes('/auth/verify-email') ||
                          originalRequest.url?.includes('/auth/refresh') ||
                          originalRequest.url?.includes('/auth/logout');

    const authStore = useAuthStore();

    // If unauthorized and not already retrying and not an auth endpoint
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // If user is already logged out, just redirect to login without trying to refresh
      if (!authStore.isAuthenticated) {
        router.push('/login');
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      // If already refreshing, wait for the refresh to complete
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh(() => {
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        await authStore.refreshTokens();
        isRefreshing = false;

        // Notify all waiting requests (cookies are updated automatically)
        onTokenRefreshed();

        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshFailed();

        // Refresh failed, logout and redirect
        authStore.logout();
        router.push('/login');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { api };
